/** Slovenské názvy stavov objednávky pre administráciu (kódy zostávajú v databáze po anglicky). */

export type Tone = 'new' | 'info' | 'progress' | 'ok' | 'done' | 'warn' | 'bad' | 'muted'

export const ORDER_STATUSES: Array<{ code: string; label: string; tone: Tone }> = [
  { code: 'NEW', label: 'Nová', tone: 'new' },
  { code: 'CONFIRMED', label: 'Potvrdená', tone: 'info' },
  { code: 'IN_PRODUCTION', label: 'Vo výrobe', tone: 'progress' },
  { code: 'READY', label: 'Pripravená', tone: 'ok' },
  { code: 'PICKED_UP', label: 'Vyzdvihnutá', tone: 'done' },
  { code: 'DELIVERED', label: 'Doručená', tone: 'done' },
  { code: 'REJECTED', label: 'Odmietnutá', tone: 'bad' },
  { code: 'CANCELLED', label: 'Zrušená', tone: 'muted' },
]

export const SCHEDULE_STATUSES: Array<{ code: string; label: string; tone: Tone }> = [
  { code: 'PENDING', label: 'Čaká na potvrdenie', tone: 'warn' },
  { code: 'CONFIRMED', label: 'Termín potvrdený', tone: 'ok' },
  { code: 'ALTERNATIVE_PROPOSED', label: 'Navrhnutý iný termín', tone: 'info' },
  { code: 'REJECTED', label: 'Termín odmietnutý', tone: 'bad' },
]

const LEGACY = { label: 'Starší záznam', tone: 'muted' as Tone }

export function orderStatus(code?: string | null) {
  return ORDER_STATUSES.find(s => s.code === code) ?? { code: code ?? '', label: code || '—', tone: 'muted' as Tone }
}

export function scheduleStatus(code?: string | null) {
  if (!code || code === 'LEGACY_UNREVIEWED') return { code: code ?? 'LEGACY_UNREVIEWED', ...LEGACY }
  return SCHEDULE_STATUSES.find(s => s.code === code) ?? { code, label: code, tone: 'muted' as Tone }
}

export function paymentTone(status?: string) : Tone {
  if (status === 'PAID') return 'ok'
  if (status === 'FAILED' || status === 'EXPIRED') return 'bad'
  if (status === 'PENDING') return 'warn'
  return 'muted'
}

/** Termín objednávky na zobrazenie: potvrdený > navrhnutý > požadovaný > historický. */
export function orderTerm(order: { confirmedFulfillmentAt?: string | null; proposedFulfillmentAt?: string | null; requestedFulfillmentAt?: string | null; pickupAt?: string | null }) {
  if (order.confirmedFulfillmentAt) return { at: order.confirmedFulfillmentAt, kind: 'potvrdený' }
  if (order.proposedFulfillmentAt) return { at: order.proposedFulfillmentAt, kind: 'návrh' }
  if (order.requestedFulfillmentAt) return { at: order.requestedFulfillmentAt, kind: 'požadovaný' }
  if (order.pickupAt) return { at: order.pickupAt, kind: 'historický' }
  return null
}

const TZ = 'Europe/Bratislava'
const dayKey = (date: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(date)

/** „Dnes 14:30“, „Zajtra 9:00“, „pi 31. 10. 10:00“. */
export function friendlyDateTime(value: string, now = new Date()): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  const time = date.toLocaleTimeString('sk-SK', { timeZone: TZ, hour: 'numeric', minute: '2-digit' })
  const day = dayKey(date)
  const today = dayKey(now)
  const tomorrow = dayKey(new Date(now.getTime() + 86400000))
  const yesterday = dayKey(new Date(now.getTime() - 86400000))
  if (day === today) return `Dnes ${time}`
  if (day === tomorrow) return `Zajtra ${time}`
  if (day === yesterday) return `Včera ${time}`
  const weekday = date.toLocaleDateString('sk-SK', { timeZone: TZ, weekday: 'short' })
  const short = date.toLocaleDateString('sk-SK', { timeZone: TZ, day: 'numeric', month: 'numeric', ...(day.slice(0, 4) !== today.slice(0, 4) ? { year: 'numeric' } : {}) })
  return `${weekday} ${short} ${time}`
}

/** Začiatok / koniec dňa (YYYY-MM-DD) v Bratislave ako ISO čas pre filter. */
export function dayStartIso(day: string): string {
  return zonedIso(day, 0, 0, 0, 0)
}
export function dayEndIso(day: string): string {
  return zonedIso(day, 23, 59, 59, 999)
}
function zonedIso(day: string, h: number, m: number, s: number, ms: number): string {
  const [y, mo, d] = day.split('-').map(Number)
  const guess = Date.UTC(y, mo - 1, d, h, m, s, ms)
  // posun časového pásma Bratislavy v ten deň (CET/CEST)
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: TZ, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(new Date(guess))
  const get = (t: string) => Number(parts.find(p => p.type === t)?.value)
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'), ms)
  return new Date(guess - (asUtc - guess)).toISOString()
}
